<template>
  <v-select
    v-bind="$attrs"
    :filter="filterFunction"
    :selectable="isSelectable"
    v-on="$listeners"
  >
    <template
      v-if="$attrs.multiple !== undefined"
      #list-header
    >
      <div class="d-flex custom-options">
        <div class="flex-grow-1">
          <b-button
            size="sm"
            variant="outline-primary"
            block
            @click="selectAll"
          >
            Select All
          </b-button>
        </div>
        <div class="flex-grow-1">
          <b-button
            size="sm"
            variant="outline-primary"
            block
            @click="clearAll"
          >
            Clear All
          </b-button>
        </div>
      </div>
    </template>

    <!-- Custom option template to show disabled state -->
    <template #option="option">
      <div :class="{ 'disabled-option': isOptionDisabled(option) }">
        {{ getOptionLabel(option) }}
      </div>
    </template>
  </v-select>
</template>

<script>
import { BButton } from 'bootstrap-vue'
import vSelect from 'vue-select'

export default {
  components: {
    vSelect,
    BButton,
  },
  computed: {
    filterFunction() {
      // Use the filter prop if provided, otherwise use the default custom filter
      return this.$attrs.filter || this.customFilter
    },
  },
  methods: {
    // Check if an option is disabled
    isOptionDisabled(option) {
      // Handle both object and primitive types
      if (typeof option === 'object' && option !== null) {
        return option.disabled || option.$isDisabled || false
      }
      return false
    },

    // Function to determine if an option is selectable (used by v-select)
    isSelectable(option) {
      return !this.isOptionDisabled(option)
    },

    // Get the label text for an option
    getOptionLabel(option) {
      const { label } = this.$attrs

      // Handle string arrays like ["CREATED", "DELETED"]
      if (typeof option === 'string') {
        return option
      }

      // Handle object arrays
      if (typeof option === 'object' && option !== null) {
        if (label && option[label] !== undefined) {
          return option[label]
        }
        // Fallback to common label properties
        if (option.text !== undefined) {
          return option.text
        }
        if (option.label !== undefined) {
          return option.label
        }
        if (option.name !== undefined) {
          return option.name
        }
        // Last resort for objects
        return JSON.stringify(option)
      }

      // Handle numbers and other primitives
      return String(option)
    },

    selectAll() {
      const { options } = this.$attrs
      const { reduce } = this.$attrs

      // Filter out disabled options when selecting all
      let selectableOptions = options.filter(option => !this.isOptionDisabled(option))

      if (reduce && typeof reduce === 'function') {
        selectableOptions = selectableOptions.map(reduce)
      }
      this.$emit('input', selectableOptions)
    },

    clearAll() {
      this.$emit('input', [])
    },

    customFilter(options, search) {
      if (!search || search.trim() === '') {
        return options
      }

      return options.filter(option => {
        let searchableText = ''

        // Use the same logic as getOptionLabel for consistency
        if (typeof option === 'string') {
          searchableText = option.toLowerCase()
        } else if (typeof option === 'object' && option !== null) {
          const { label } = this.$attrs
          if (label && option[label] !== undefined) {
            searchableText = String(option[label]).toLowerCase()
          } else if (option.text !== undefined) {
            searchableText = String(option.text).toLowerCase()
          } else if (option.label !== undefined) {
            searchableText = String(option.label).toLowerCase()
          } else if (option.name !== undefined) {
            searchableText = String(option.name).toLowerCase()
          } else {
            searchableText = JSON.stringify(option).toLowerCase()
          }
        } else {
          searchableText = String(option).toLowerCase()
        }

        // Split the search query into individual words
        const searchWords = search.toLowerCase().trim().split(/\s+/)

        // Check if all search words exist in the searchable text
        return searchWords.every(word => searchableText.includes(word))
      })
    },
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
</style>

<style scoped>
.custom-options {
  column-gap: 10px;
  margin: 10px;
}

.disabled-option {
  opacity: 0.6;
  cursor: not-allowed;
  background-color: #ffebee;
  color: #d32f2f !important;
}

.disabled-option:hover {
  background-color: #ffcdd2 !important;
  color: #c62828 !important;
}

/* Override v-select's hover styles for disabled options */
:deep(.vs__dropdown-option.disabled-option) {
  background-color: #ffebee !important;
  color: #d32f2f !important;
}

:deep(.vs__dropdown-option.disabled-option:hover) {
  background-color: #ffcdd2 !important;
  color: #c62828 !important;
}
</style>
